import { AppUtil, DateUtil, DateWrapper, LocalStorageUtil, E_LocalStorageKey } from 'kuaidong-lib'

import {
  Clazz,
  EverydayHomeworkPlan,
  HomeworkClazzStatDaily,
  HomeworkClazzStatWeekly,
  HomeworkComment,
  HomeworkCommentTemplate,
  HomeworkDailyStat,
  HomeworkPlan,
  HomeworkPlanForm,
  HomeworkRecord,
  HomeworkReport,
  HomeworkStudent,
  HomeworkStudentWeekly,
  HomeworkTemplate,
  IHomeworkDrillDetail,
  IHomeworkIntelligentTarget,
  IHomeworkIntelligentRuleOption,
  IHomeworkIntelligentRulePostParams,
  HomeworkEditPlan,
  IAutoCorrectionConfig,
  IGradeReivew,
} from 'kuaidong-model'

const templateHandler = (res: any) => {
  const drills = Array(8).fill([])
  res.homework_contents.forEach((v: any) => {
    const idx = !v.day_of_week || v.day_of_week === 8 ? 0 : v.day_of_week
    drills[idx] = v.homework_items.map((d: any) => ({
      drill_id: d.drill_id,
      repeats: d.repeats,
      container_type: d.container_type,
      homework_desc: d.homework_desc,
      gender_scope: d.gender_scope,
      stage: d.stage,
    }))
  })
  return {
    id: res.hw_tpl_id,
    title: res.title,
    desc: res.description,
    grades: res.grades,
    message: res.message,
    repeatBy: res.repeat_type,
    fridayChecked: res.is_check_wd5,
    drills: drills,
  }
}

const templateListHandler = (res: { hw_tpls: any[] }) => {
  return res.hw_tpls.map((v) => ({
    id: v.hw_tpl_id,
    title: v.title,
    type: v.repeat_type,
    desc: v.description,
    date: new DateWrapper(v.created_time).showFullStr,
  }))
}

export class AHomework {
  static fetchDayClassResult(
    everyday_release_id: number,
    class_id: number,
    curr_date: Date,
    limit: number,
    offset: number
  ) {
    return AppUtil.httpGET('/web/homework/students/list', {
      everyday_release_id,
      class_id,
      curr_date: new DateWrapper(curr_date).showDateStr,
      limit,
      offset,
    })
  }

  static async fetchWeeklyCount(
    class_id: number,
    date: Date,
    scopeCover: 'gymnastics' | null = null
  ): Promise<{ count: number }[]> {
    return AppUtil.httpGET('/web/homework/count', {
      class_id,
      date: new DateWrapper(date).showDateStr,
      scope_cover: scopeCover,
    })
  }

  static async fetchWeeklyLineData(
    class_id: number,
    date: Date,
    scopeCover: 'gymnastics' | null = null
  ): Promise<{ rate: number }[]> {
    return AppUtil.httpGET('/web/homework/week/completion', {
      class_id,
      date: new DateWrapper(date).showDateStr,
      scope_cover: scopeCover,
    })
  }

  static async fetchHomeworkPlans(): Promise<HomeworkPlan[]> {
    return AppUtil.httpGET('/cel/v2/homework/plan/list')
  }

  static async fetchHomeworkSummaryDaily(
    clazzId: number,
    date: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<HomeworkClazzStatDaily> {
    return AppUtil.httpGET('/class/homework/inspect', {
      class_id: clazzId,
      target_date: date,
      scope_cover: scopeCover,
    })
  }

  static async fetchHomeworkSummariesDailyLatest(): Promise<HomeworkClazzStatDaily[]> {
    return AppUtil.httpGET('/homework/home_page/classes/list')
  }

  static async fetchHomeworkSummariesDaily(
    clazzId?: number,
    startDate?: string,
    endDate?: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<HomeworkClazzStatDaily[]> {
    return AppUtil.httpGET(
      '/v2/homework/classes/list',
      {
        class_id: clazzId,
        start_date: startDate,
        end_date: endDate,
        scope_cover: scopeCover,
      },
      {},
      true
    )
  }

  static async exportHomeworkSummariesDaily(
    clazzId?: number,
    startDate?: string,
    endDate?: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<string> {
    return AppUtil.httpGET('/cel/classes/homework/train/stats/excel', {
      class_id: clazzId,
      start_date: startDate,
      end_date: endDate,
      scope_cover: scopeCover,
    })
  }

  static async fetchHomeworkSummariesWeekly(
    startDate: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<HomeworkClazzStatWeekly[]> {
    return AppUtil.httpGET('/cel/weekly/homework/classes/list', {
      start_date: startDate,
      scope_cover: scopeCover,
    })
  }

  static async checkHomeworkPlanSave(form: HomeworkPlanForm, isShs = false): Promise<boolean> {
    const res = await AppUtil.httpGET('/homework_schedule/overlap', {
      class_ids: isShs ? form.classes : form.classes.map((c) => c.id),
      start_time: form.startDate,
      end_time: form.endDate,
    })

    return res
  }

  static async saveHomeworkPlan(
    form: HomeworkPlanForm,
    clearAndSync = false,
    saveTemplate = true,
    cascade = false
  ): Promise<void> {
    const params = {
      schedule_id: form.schedule_id,
      group_id: form.groupId,
      release_id: form.homeworkContents[0].release_id || null,
      class_ids: form.classes.map((c) => c.id),
      is_check_wd5: form.fridayChecked,
      message: form.message?.trim() || null,
      start_time: form.startDate,
      expire_time: form.endDate,
      end_time: form.endDate,
      homework_contents: form.homeworkContents.filter((c) => c.homework_items.length > 0),
      release_type: form.releaseType,
      is_publish_all_class: form.publishToAllClasses,
      is_clear_and_sync: clearAndSync,
      template_id: form.template_id,
      is_modify: form.is_modify,
      repeat_type: form.repeatBy,
      is_record_custom_tpl: saveTemplate && form.is_modify !== false,
      hw_tpl_id: form.hw_tpl_id,
      is_clear_today: form.is_clear_today,
      cascade,
      completion_start_time: form.completion_start_time,
      completion_end_time: form.completion_end_time,
    }

    let url = '/v2/homework/publish'
    const inviteCode = LocalStorageUtil.getLocalStorage(E_LocalStorageKey.activityInviteCode) || ''
    if (inviteCode) {
      url += `?invite_code=${inviteCode}`
      LocalStorageUtil.removeLocalStorage(E_LocalStorageKey.activityInviteCode)
    }
    const actId = LocalStorageUtil.getLocalStorage(E_LocalStorageKey.activityIdForHomework) || ''
    if (actId) {
      url += `${url.includes('?') ? '&' : '?'}activity_id=${actId}`
      LocalStorageUtil.removeLocalStorage(E_LocalStorageKey.activityIdForHomework)
    }

    return AppUtil.httpPOST(url, params)
  }

  static async checkHomeworkToday(groupId: number, releaseId: number): Promise<boolean> {
    return AppUtil.httpGET('/cel/v2/homework/check/delete_close/homework', { group_id: groupId, release_id: releaseId })
  }

  static async removeHomeworkPlan(groupId: number, releaseId: number, checked: boolean): Promise<void> {
    return AppUtil.httpDelete('/v2/homework/publish', {
      group_id: groupId,
      release_id: releaseId,
      is_del_curr_homework: checked,
    })
  }

  static async checkHomeworkPlanPublish(groupId: number, releaseId: number): Promise<Clazz[]> {
    const { classes } = await AppUtil.httpGET('/cel/v2/homework/check/classes', {
      group_id: groupId,
      release_id: releaseId,
    })
    return classes || []
  }

  static async publish(groupId: number, releaseId: number): Promise<void> {
    return AppUtil.httpPUT(`/v2/homework/publish?group_id=${groupId}&release_id=${releaseId}`)
  }

  static async unpublish(groupId: number, releaseId: number, checked: boolean): Promise<void> {
    return AppUtil.httpPOST(
      `/v2/homework/cancel?group_id=${groupId}&release_id=${releaseId}&is_del_curr_homework=${checked}`
    )
  }

  static async fetchEverydayHomeworkPlan(everydayReleaseId: number, clazzId: number): Promise<EverydayHomeworkPlan> {
    return AppUtil.httpGET('/v2/everyday/homework/release', {
      everyday_release_id: everydayReleaseId,
      class_id: clazzId,
    })
  }

  static async removeEveryHomeworkPlan(everydayReleaseId: number): Promise<void> {
    return AppUtil.httpDelete('/v2/everyday/homework/release', { everyday_release_id: everydayReleaseId })
  }

  static async fetchHomeworkStudents(
    classId: number,
    currentDate: string,
    everydayReleaseId: number
  ): Promise<HomeworkStudent[]> {
    return AppUtil.httpGET('/v2/homework/students/list', {
      class_id: classId,
      curr_date: currentDate,
      everyday_release_id: everydayReleaseId,
    })
  }

  static async fetchHomeworkStudentsWeekly(
    clazzId: number,
    startDate: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<HomeworkStudentWeekly[]> {
    return AppUtil.httpGET('/cel/weekly/homework/students/list', {
      class_id: clazzId,
      start_date: startDate,
      scope_cover: scopeCover,
    })
  }

  static async fetchHomeworkComments(everydayReleaseId: number, userId: string): Promise<HomeworkComment[]> {
    return AppUtil.httpGET('/v2/homework/comments/list', {
      everyday_release_id: everydayReleaseId,
      user_id: userId,
    })
  }

  static async fetchHomeworkRecord(recordId: string): Promise<HomeworkRecord> {
    return AppUtil.httpGET(`/v2/homework/student/record?record_id=${recordId}`)
  }

  static async fetchHomeworkRecords(
    everydayReleaseId: number,
    userId: string,
    homeworkIds: number[]
  ): Promise<HomeworkRecord[]> {
    return AppUtil.httpGET('/v2/homework/student/record/list', {
      everyday_release_id: everydayReleaseId,
      user_id: userId,
      homework_ids: homeworkIds,
    })
  }

  static async fetchHomeworkRecordsWeekly(
    userId: string,
    startDate: string,
    scopeCover: 'gymnastics' | null = null
  ): Promise<HomeworkRecord[]> {
    return AppUtil.httpGET('/cel/homework/weekly/stu_record/list', {
      user_id: userId,
      start_date: startDate,
      scope_cover: scopeCover,
    })
  }

  static async sendComment(releaseId: number, userIds: string[], comments: string) {
    const params = {
      user_ids: userIds,
      everyday_release_id: releaseId,
      comments,
    }

    return AppUtil.httpPOST('/v2/homework/add/comment', params)
  }

  static async fetchCommentTemplates(): Promise<HomeworkCommentTemplate[]> {
    return AppUtil.httpGET('/v2/homework/review_tpl')
  }

  static async fetchCommentTemplatesOfGrades(): Promise<IGradeReivew[]> {
    return AppUtil.httpGET('/v2/homework/review_tpl/grades')
  }

  static async switchGradeCommentStatus(scope: number, status: boolean) {
    return AppUtil.httpPOST('/v2/homework/review_tpl/status', { scope, status })
  }

  static async addCommentTemplate(text: string, scope: number | null = null) {
    return AppUtil.httpPOST('/v2/homework/review_tpl', { txt: text, scope })
  }

  static async removeCommentTemplate(text: string, scope: number | null = null) {
    return AppUtil.httpDelete('/v2/homework/review_tpl', { txt: text, scope })
  }

  static async like(everydayReleaseId: number, userIds: string[]): Promise<void> {
    const params = {
      user_ids: userIds,
    }

    return AppUtil.httpPOST(`/async/v2/homework/touch/like?everyday_release_id=${everydayReleaseId}`, params)
  }

  static async homeworkRecordCorrect(recordId: string, result: string) {
    return AppUtil.httpPOST('/v2/homework/add/record_correct', { record_id: recordId, result })
  }

  static async fetchClassesWithWinterHolidayHomeworks(): Promise<Clazz[]> {
    return AppUtil.httpGET('/cel/v2/homework/class/check/winter_2020')
  }

  static async fetchHomeworkReport(date: string): Promise<HomeworkReport> {
    return AppUtil.httpGET('/cel/classes/homework/report', { date })
  }

  static async addRemark(everydayReleaseId: number, userId: string, status: string | null, text: string | null) {
    const params = {
      everyday_release_id: everydayReleaseId,
      user_id: userId,
      special_status: status,
      remark: text,
    }
    return AppUtil.httpPOST('/async/v2/homework/add/remark', params)
  }

  static async remindUncompleteStudents(everydayReleaseId: number, clazzId: number) {
    return AppUtil.httpGET('/async/homework/notify/uncomplete/stus', {
      everyday_release_id: everydayReleaseId,
      class_id: clazzId,
    })
  }

  static async remindUncompleteStudentsToday(clazzId: number) {
    return AppUtil.httpGET('/async/homework/weekly/notify', { class_id: clazzId })
  }

  static async remindUncompleteStudentsBatch(everydayReleaseIds: number[]) {
    return AppUtil.httpPOST('/async/homework/notify/classes/uncomplete', { everyday_release_ids: everydayReleaseIds })
  }

  static async fetchHomeworkDailyStat(everydayReleaseId: number): Promise<HomeworkDailyStat> {
    return AppUtil.httpGET('/cel/v2/homework/daily/stat', { everyday_release_id: everydayReleaseId })
  }

  static async fetchHomeworkWeeklyStat(clazzId: number, startDate: string, scopeCover: 'gymnastics' | null = null) {
    return AppUtil.httpGET('/cel/homework/weekly/summary/stat', {
      class_id: clazzId,
      start_date: startDate,
      scope_cover: scopeCover,
    })
  }

  static async fetchHomeworkTemplates(): Promise<HomeworkTemplate[]> {
    return AppUtil.httpGET('/v2/homework/config/templates', {}, {}, true)
  }

  public static async fetchTemplate(template_id: string): Promise<HomeworkTemplate> {
    return AppUtil.httpGET('/v2/homework/config/template', { template_id })
  }

  static async generateHomeworkPlanByQuestionnaire(questionnaireAnswers: object) {
    return AppUtil.httpPOST('/cel/hw_plan/templates', questionnaireAnswers)
  }

  static async publishHomeworkPlanByQuestionnaire(questionnaireAnswers: object, comments: { [index: number]: string }) {
    return AppUtil.httpPOST('/async/hw_plan/create_from_tpl', { ...questionnaireAnswers, grade_message_map: comments })
  }

  static async templateList() {
    return AppUtil.httpGET('/cel/hw/custom/tpl/list').then(templateListHandler)
  }

  static async rawTemplateList() {
    return AppUtil.httpGET('/cel/hw/custom/tpl/list')
  }

  static async fetchTpl(hw_tpl_id: string) {
    return AppUtil.httpGET('/cel/hw/custom/tpl/single', { hw_tpl_id }).then(templateHandler)
  }

  static async updateTpl(template: any) {
    return AppUtil.httpPOST('/cel/hw/custom/tpl/single', {
      hw_tpl: {
        id: template.id,
        is_check_wd5: template.fridayChecked,
        title: template.title,
        grades: template.grades || [],
        repeat_type: template.repeatBy,
        description: template.desc,
        message: template.message,
      },
      homework_contents: template.drills
        .map((v: any[], idx: number) => ({
          homework_items: v.map((d) => ({
            drill_id: d.drill_id,
            repeats: d.repeats,
            gender_scope: d.gender_scope,
            stage: d.stage,
          })),
          day_of_week: idx || null,
        }))
        .filter((v: any) => v.homework_items.length > 0),
    })
  }

  static async deleteTpl(hw_tpl_id: string) {
    return AppUtil.httpDelete('/cel/hw/custom/tpl/single', { hw_tpl_id })
  }

  static async fetchHomeworkDrillDetail(drillId: string): Promise<IHomeworkDrillDetail> {
    return AppUtil.httpGET('/global/config/drill', { drill_id: drillId })
  }

  static async fetchHomeworkIntelligentTargets(): Promise<IHomeworkIntelligentTarget[]> {
    return AppUtil.httpGET('/smart_homework/targets')
  }

  static async fetchHomeworkIntelligentRules(
    targetRuleInfo: IHomeworkIntelligentRulePostParams
  ): Promise<IHomeworkIntelligentRuleOption> {
    return AppUtil.httpPOST('/smart_homework/rule', { ...targetRuleInfo })
  }

  static checkConflict(class_ids: number[]) {
    return AppUtil.httpPOST('/smart_homework/check', { class_ids })
  }

  static publishPost(
    form: HomeworkEditPlan,
    clearTody = false,
    saveTemplate = false,
    cascade = false
  ): Promise<{
    schedule_ids: string[]
  }> {
    return AppUtil.httpPOST('/v2/homework/publish', {
      schedule_id: form.schedule_id,
      class_ids: form.classes,
      message: form.message?.trim() || null,
      start_time: form.startDate ? new DateWrapper(DateUtil.beginningOfDay(form.startDate)).showFullStr : null,
      end_time: form.endDate ? new DateWrapper(DateUtil.endOfDay(form.endDate)).showFullStr : null,
      homework_contents: form.content
        .map((v, idx) => {
          const homeworkItems = v.map((t) => ({
            drill_id: t.id,
            repeats: t.count,
            container_type: t.container,
            homework_desc: t.category,
            gender_scope: t.gender,
            stage: t.stage || '主项目',
          }))

          return {
            homework_items: [
              ...homeworkItems.filter((item) => item.stage === '热身'),
              ...homeworkItems.filter((item) => item.stage === '主项目' || !item.stage),
              ...homeworkItems.filter((item) => item.stage === '拉伸'),
            ],
            day_of_week: idx || undefined,
          }
        })
        .slice(...{ day: [0, 1], week: [1], weekend: [6], weekends_only: [6] }[form.type])
        .filter((c) => c.homework_items.length),
      repeat_type: form.type,
      is_record_custom_tpl: saveTemplate,
      is_clear_today: clearTody,
      is_check_wd5: false,
      template_id: null,
      cascade,
      completion_start_time: form.completion_start_time,
      completion_end_time: form.completion_end_time,
    })
  }

  static deleteIllegalRecordVideo(record_id: string, reason: string) {
    return AppUtil.httpDelete('/v2/homework/record/video', {
      record_id,
      reason,
    })
  }

  static async fetchHomeworkUneditable(): Promise<{ start: number; end: number }> {
    return AppUtil.httpGET('/homework/unavailable_timescope')
  }

  static async fetchAutoCorrectionConfig(): Promise<IAutoCorrectionConfig> {
    return AppUtil.httpGET('/v2/homework/auto_correction_config')
  }

  static async setAutoCorrectionConfig(config: IAutoCorrectionConfig): Promise<any> {
    return AppUtil.httpPUT('/v2/homework/auto_correction_config', config)
  }
}

export type tplListType = ReturnType<typeof templateListHandler>
